import { Autocomplete } from '@scale/scaleui';
import _ from 'lodash';
import { useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  podTypeFilterSelector,
  useStatContextStore,
} from 'react-scale/components/ExpertManagement/stores/statContext.store';
import shallow from 'zustand/shallow';
import { fetchWorkerManagers } from './api';

interface ISelectWorkerManagerProps {
  placeholder?: string;
  selectedManagerId: string;
  setSelectedManagerId: (managerId: string) => void;
  className?: string;
  ignorePodType?: boolean;
}

export const SelectWorkerManager: React.FC<ISelectWorkerManagerProps> = ({
  placeholder,
  selectedManagerId,
  setSelectedManagerId,
  className,
  ignorePodType,
}) => {
  const podTypeFilter = useStatContextStore(podTypeFilterSelector, shallow);
  const queryKey = ignorePodType ? ['fetchWorkerManagers'] : ['fetchWorkerManagers', podTypeFilter];
  const workerManagersQuery = useQuery(queryKey, () =>
    fetchWorkerManagers(ignorePodType ? '' : podTypeFilter),
  );

  const workerManagerOptions = useMemo(() => {
    const managers = workerManagersQuery.data || [];
    return {
      byEmail: Object.fromEntries(
        _.sortBy(managers, 'email').map((manager: any) => [
          `${manager.email} (${manager.role})`,
          manager.id,
        ]),
      ),
      byId: Object.fromEntries(
        managers.map((manager: any) => [manager.id, `${manager.email} (${manager.role})`]),
      ),
    };
  }, [workerManagersQuery.data]);

  const onSelect = useCallback(
    (managerEmail: string) => {
      setSelectedManagerId(workerManagerOptions.byEmail[managerEmail]);
    },
    [setSelectedManagerId, workerManagerOptions],
  );

  return (
    <Autocomplete
      options={Object.keys(workerManagerOptions.byEmail)}
      disabled={workerManagersQuery.isLoading}
      value={workerManagerOptions.byId[selectedManagerId] || ''}
      setSelectedOption={onSelect}
      canCreateOption={false}
      inputProps={{ placeholder: placeholder ?? 'Enter manager email...' }}
      className={className}
    />
  );
};
